import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useRoute } from '@react-navigation/native';
import { useScreenLoad } from '../../shared/ui/hooks-screen/useScreenLoad';
import type { ScreenRefreshResult } from '../../shared/ui/screenRefreshFeedback';
import { getLocalYmdRangeFromPreset, ymdToEndIso, ymdToStartIso } from '../../sales/public-api'
import type { DateRangePreset } from '../../sales/public-api';
import { getSessionOrganizationId } from '../../shell/session/sessionOrganization';
import { useSessionStore } from '../../shell/session/store/useSessionStore';
import type { OrderHistoryKind } from '../domain/orderQueue';
import type { CustomerOrderRow } from '../domain/orderTypes';
import { fetchOrderHistory } from '../infra/ordersRepo';

const PAGE_SIZE = 30;

export type HistoryKindFilter = Exclude<OrderHistoryKind, 'closed'> | 'closed';

export const HISTORY_KIND_KEYS: HistoryKindFilter[] = ['completed', 'cancelled', 'closed'];

export const HISTORY_KIND_LABELS: Record<HistoryKindFilter, string> = {
  completed: 'Completados',
  cancelled: 'Cancelados',
  closed: 'Todos',
};

export const HISTORY_DATE_PRESETS: DateRangePreset[] = ['today', 'last_7_days', 'this_month'];

export const HISTORY_DATE_PRESET_LABELS: Partial<Record<DateRangePreset, string>> = {
  today: 'Hoy',
  last_7_days: 'Últimos 7 días',
  this_month: 'Este mes',
};

export function useOrdersHistoryScreen() {
  const route = useRoute();
  const branchId = useSessionStore((s) => s.currentBranchId);
  const [orders, setOrders] = useState<CustomerOrderRow[]>([]);
  const [kind, setKind] = useState<HistoryKindFilter>('completed');
  const [datePreset, setDatePreset] = useState<DateRangePreset>('today');
  const [hasMore, setHasMore] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  const [orgId, setOrgId] = useState<string | null>(null);
  const [initialLoading, setInitialLoading] = useState(true);
  const skipReload = useRef(true);
  const offsetRef = useRef(0);

  const range = useMemo(() => getLocalYmdRangeFromPreset(datePreset), [datePreset]);

  const loadPage = useCallback(
    async (mode: 'replace' | 'append'): Promise<void> => {
      const oid = await getSessionOrganizationId();
      setOrgId(oid);
      if (!oid || !branchId) {
        setOrders([]);
        setHasMore(false);
        return;
      }

      const offset = mode === 'replace' ? 0 : offsetRef.current;
      const result = await fetchOrderHistory(oid, branchId, {
        fromIso: ymdToStartIso(range.fromYmd),
        toIso: ymdToEndIso(range.toYmd),
        kind,
        limit: PAGE_SIZE,
        offset,
      });

      if (mode === 'replace') {
        setOrders(result.rows);
        offsetRef.current = result.rows.length;
      } else {
        setOrders((prev) => {
          const seen = new Set(prev.map((r) => r.id));
          const merged = [...prev];
          for (const row of result.rows) {
            if (!seen.has(row.id)) merged.push(row);
          }
          offsetRef.current = merged.length;
          return merged;
        });
      }
      setHasMore(result.hasMore);
    },
    [branchId, kind, range.fromYmd, range.toYmd],
  );

  const loadFn = useCallback(
    async (_opts?: { force?: boolean }): Promise<ScreenRefreshResult | void> => {
      try {
        await loadPage('replace');
      } finally {
        setInitialLoading(false);
      }
    },
    [loadPage],
  );

  const { refreshing, onRefresh } = useScreenLoad(route.name, loadFn, {
    feedback: false,
    minFocusIntervalMs: 30_000,
  });

  useEffect(() => {
    if (skipReload.current) {
      skipReload.current = false;
      return;
    }
    void onRefresh();
  }, [branchId, kind, datePreset, onRefresh]);

  const loadMore = useCallback(async () => {
    if (!hasMore || loadingMore || refreshing || initialLoading) return;
    setLoadingMore(true);
    try {
      await loadPage('append');
    } finally {
      setLoadingMore(false);
    }
  }, [hasMore, initialLoading, loadPage, loadingMore, refreshing]);

  return {
    orders,
    refreshing,
    initialLoading,
    loadingMore,
    hasMore,
    onRefresh,
    loadMore,
    kind,
    setKind,
    datePreset,
    setDatePreset,
    orgId,
    branchId,
  };
}
